import { useEffect, useId, useRef } from 'react';
import { useTokens } from './theme';

const FOCUSABLE = [
  'a[href]', 'button:not([disabled])', 'textarea:not([disabled])',
  'input:not([disabled])', 'select:not([disabled])', '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * Accessible DOM modal (NEW behavior the RN Modal lacked): backdrop with
 * click-outside + Escape to close, focus trap, focus return to the opener on
 * close, role="dialog" aria-modal. Theme-aware surface.
 *
 * @param {{ open: boolean, onClose: () => void, title?: React.ReactNode, children: React.ReactNode }} props
 */
export function Modal({
  open, onClose, title, children, initialFocusRef,
}) {
  const t = useTokens();
  const cardRef = useRef(null);
  const openerRef = useRef(null);
  const titleId = useId();
  // Held in a ref so a caller passing an inline arrow does not re-run the
  // focus effect below on every render (that stole focus back to the first
  // field on each keystroke).
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; });

  useEffect(() => {
    if (!open) { return undefined; }
    openerRef.current = document.activeElement;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCloseRef.current?.();
        return;
      }
      if (e.key !== 'Tab') { return; }
      const nodes = cardRef.current?.querySelectorAll(FOCUSABLE);
      if (!nodes || nodes.length === 0) {
        e.preventDefault();
        cardRef.current?.focus();
        return;
      }
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && (active === first || active === cardRef.current)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    // Focus what the CALLER nominates, falling back to the first focusable node.
    //
    // "First focusable" is a poor default for any dialog whose primary action is
    // typing: in the MFA dialog the first node is "Copy key", so the caller had
    // to yank focus back with its own effect afterwards — a race that left the
    // code field unfocused after a keystroke and made the code enterable only
    // one character per click.
    const nodes = cardRef.current?.querySelectorAll(FOCUSABLE);
    const target = initialFocusRef?.current
      || (nodes && nodes.length ? nodes[0] : cardRef.current);
    target?.focus();

    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      if (openerRef.current && typeof openerRef.current.focus === 'function') {
        openerRef.current.focus();
      }
    };
  }, [open, initialFocusRef]);

  if (!open) { return null; }

  return (
    <div
      role="presentation"
      onMouseDown={(e) => { if (e.target === e.currentTarget) { onClose?.(); } }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(17,17,17,0.45)' }}
    >
      <div
        ref={cardRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        tabIndex={-1}
        className="w-full max-w-[420px] rounded-xl border p-6 focus:outline-none"
        style={{ backgroundColor: t.surface, borderColor: t.border }}
      >
        {title ? (
          <h2
            id={titleId}
            className="text-lg font-semibold mb-2"
            style={{ fontFamily: t.fonts.display, color: t.heading }}
          >
            {title}
          </h2>
        ) : null}
        {children}
      </div>
    </div>
  );
}

export default Modal;
